// ============================================================
// TMR Portal Admin — aba Conta dentro do detalhe da empresa (13)
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Field } from '../../components/ui';
import { SelectField, TextField } from '../../components/fields';
import { ComboField } from '../../components/ComboField';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  useAddReceivingAccount,
  useReceivingAccounts,
  useBanks,
  useSetDefaultReceivingAccount,
  useUpdateReceivingAccount,
} from '../../data/finance/queries';
import { validateBankAccount } from '../../data/finance/form';
import type { BankAccountDetailsInput, ReceivingAccount } from '../../data/finance/contract';

/** Props for {@link CompanyAccountTab}. */
interface CompanyAccountTabProps {
  companyId: string;
  /** Whether the "Nova conta" modal is open — the button lives in the screen header. */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
}

/**
 * The Empresa's receiving accounts — where its money lands (`13`).
 *
 * A section of its own rather than a card on Visão geral, and the move is not only about space. As a card
 * it could show one account and edit it only from inside the Empresa's cadastre editor, which meant two
 * capabilities the backend has always had were unreachable: an Empresa may hold **several** accounts, and
 * which one is the default is its own decision — a change of where the money goes, deliberately not a side
 * effect of correcting a branch number.
 *
 * It has the four states in its own right because it is its own request: a missing `finance.read` reads as
 * "sem permissão", never as "sem conta" — telling somebody the Empresa has no account when they simply may
 * not see it would be worse than saying nothing.
 */
export function CompanyAccountTab({ companyId, creating, onCreatingChange }: CompanyAccountTabProps) {
  const [editing, setEditing] = useState<ReceivingAccount | null>(null);
  const [promoting, setPromoting] = useState<ReceivingAccount | null>(null);

  const canRead = usePermission(PERMISSIONS.financeRead);
  const canManage = usePermission(PERMISSIONS.financeManage);

  const query = useReceivingAccounts(canRead ? companyId : undefined);
  const setDefault = useSetDefaultReceivingAccount(companyId);

  if (!canRead) {
    return (
      <div className="pa-card">
        <EmptyState
          message="Você não tem permissão para ver os dados bancários desta empresa."
          hint="Peça o acesso a quem administra os papéis."
        />
      </div>
    );
  }

  if (query.isPending) return <Skeleton rows={4} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const accounts = query.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {setDefault.error && (
        <Notice tone="danger">
          {setDefault.error instanceof ApiError
            ? setDefault.error.detail
            : 'Não foi possível trocar a conta principal.'}
        </Notice>
      )}

      {accounts.length === 0 ? (
        <div className="pa-card">
          <EmptyState
            message="Esta empresa ainda não tem conta de recebimento."
            hint={
              canManage
                ? 'Sem uma conta, não há para onde enviar os repasses.'
                : 'Você não tem permissão para cadastrar uma conta.'
            }
            action={
              canManage ? (
                <Btn variant="primary" icon={Icons.plus} onClick={() => onCreatingChange(true)}>
                  Cadastrar conta
                </Btn>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="pa-cards-masonry">
          {accounts.map((account) => (
            <div key={account.id} className="pa-card" style={{ padding: 20 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginBottom: 6,
                }}
              >
                <div className="pa-eyebrow">
                  {account.isDefault ? 'Conta principal' : 'Conta de recebimento'}
                </div>
                {canManage && (
                  <Btn variant="ghost" icon={Icons.pen} onClick={() => setEditing(account)}>
                    Editar
                  </Btn>
                )}
              </div>

              <Field label="Titular">{account.holderLegalName}</Field>
              <Field label="Banco">
                {account.bankCode} · {account.bankName}
              </Field>
              <Field label="Tipo de conta">
                {account.accountType === 'Checking' ? 'Corrente' : 'Poupança'}
              </Field>
              <Field label="Agência / Conta">
                {account.branch} / {account.number}-{account.digit}
              </Field>

              {/* Making an account the default moves where the money goes, so it is its own deliberate
                  action with its own confirmation — never a side effect of an edit (13 §8). */}
              {canManage && !account.isDefault && (
                <div style={{ marginTop: 12 }}>
                  <Btn variant="secondary" icon={Icons.check} onClick={() => setPromoting(account)}>
                    Tornar principal
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <AccountFormModal
          companyId={companyId}
          account={editing}
          firstAccount={accounts.length === 0}
          onClose={() => {
            setEditing(null);
            onCreatingChange(false);
          }}
        />
      )}

      {promoting && (
        <ConfirmDialog
          title="Tornar esta a conta principal?"
          message={
            <>
              Os repasses desta empresa passam a ir para {promoting.bankName}, agência {promoting.branch},
              conta {promoting.number}-{promoting.digit}. A conta principal de hoje deixa de ser.
            </>
          }
          confirmLabel="Tornar principal"
          icon={Icons.wallet}
          onConfirm={() => void setDefault.mutateAsync(promoting.id)}
          onClose={() => setPromoting(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link AccountFormModal}. */
interface AccountFormModalProps {
  companyId: string;
  /** The account being corrected, or null to add one. */
  account: ReceivingAccount | null;
  /** Whether this would be the Empresa's first account — which is always the default, whatever is chosen. */
  firstAccount: boolean;
  onClose: () => void;
}

/** The bank-details form. All-or-nothing, because the backend refuses a partial account. */
function AccountFormModal({ companyId, account, firstAccount, onClose }: AccountFormModalProps) {
  const [form, setForm] = useState<BankAccountDetailsInput>(() =>
    account
      ? {
          holderLegalName: account.holderLegalName,
          bankName: account.bankName,
          bankCode: account.bankCode,
          accountType: account.accountType,
          branch: account.branch,
          number: account.number,
          digit: account.digit,
        }
      : {
          holderLegalName: '',
          bankName: '',
          bankCode: '',
          accountType: 'Checking',
          branch: '',
          number: '',
          digit: '',
        },
  );
  const [showErrors, setShowErrors] = useState(false);

  const banks = useBanks();
  const add = useAddReceivingAccount(companyId);
  const update = useUpdateReceivingAccount(companyId);
  const saving = add.isPending || update.isPending;
  const saveError = add.error ?? update.error;

  const set = <K extends keyof BankAccountDetailsInput>(key: K, value: BankAccountDetailsInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  // Mirrors the domain's rules field by field, so a branch of one digit is refused next to the branch
  // instead of coming back as one unattached sentence after a round-trip. The server still decides.
  const errors = validateBankAccount(form);
  const shown = (field: keyof BankAccountDetailsInput) => (showErrors ? errors[field] : undefined);

  const submit = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;

    if (account) await update.mutateAsync({ accountId: account.id, details: form });
    // The Empresa's first account is always the default; sending anything else would be a lie the backend
    // then corrects (13 §8).
    else await add.mutateAsync({ account: form, isDefault: firstAccount });

    onClose();
  };

  return (
    <ModalShell
      title={account ? 'Editar conta de recebimento' : 'Nova conta de recebimento'}
      lead={
        account
          ? 'Corrigir os dados não muda qual conta é a principal.'
          : firstAccount
            ? 'Esta será a conta principal da empresa.'
            : 'A conta nasce como alternativa. Para trocar o destino dos repasses, use "Tornar principal".'
      }
      onClose={onClose}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <TextField
          label="Titular"
          required
          hint="A razão social exatamente como o banco a registra. Divergência aqui é o motivo mais comum de uma transferência voltar."
          value={form.holderLegalName}
          onChange={(value) => set('holderLegalName', value)}
          error={shown('holderLegalName')}
        />
        {/* One field, not two. The code and the name come from the same row of the legacy's own
            dictionary, so they cannot disagree — before this they were typed separately against a
            hard-coded list of eleven banks, and "208" was a plausible thing to put in either box. */}
        <ComboField
          label="Banco"
          required
          hint="Vem do cadastro de bancos do sistema legado. Digite o nome ou o código — o código é o que identifica o banco na transferência, por isso ele e o nome são escolhidos juntos."
          value={form.bankCode}
          onChange={(code) => {
            const bank = banks.data?.find((b) => b.code === code);
            setForm((current) => ({
              ...current,
              bankCode: code,
              // The name travels with the code, and is only ever what the catalogue says.
              bankName: bank?.name ?? '',
            }));
          }}
          options={(banks.data ?? []).map((bank) => ({
            value: bank.code,
            label: `${bank.code} · ${bank.name}`,
            // Searched too, so the registered name finds a bank the short one does not.
            secondary: bank.fullName ?? undefined,
          }))}
          placeholder={banks.isPending ? 'Carregando bancos…' : 'Busque pelo nome ou código'}
          notFoundMessage="Nenhum banco com esse nome ou código."
          error={shown('bankCode') ?? shown('bankName')}
          disabled={banks.isPending}
        />
        <SelectField
          label="Tipo de conta"
          required
          hint="Corrente ou poupança, como está no contrato com o banco. O tipo errado faz o depósito de teste falhar."
          value={form.accountType}
          onChange={(value) => set('accountType', value as BankAccountDetailsInput['accountType'])}
          options={[
            { value: 'Checking', label: 'Corrente' },
            { value: 'Savings', label: 'Poupança' },
          ]}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: 12 }}>
          <TextField
            label="Agência"
            required
            hint="De 3 a 5 dígitos, sem o dígito verificador da agência. Pontos e traços são ignorados."
            inputMode="numeric"
            placeholder="0001"
            value={form.branch}
            onChange={(value) => set('branch', value)}
            error={shown('branch')}
          />
          <TextField
            label="Conta"
            required
            hint="De 4 a 12 dígitos, sem o dígito verificador — ele vai no campo ao lado."
            inputMode="numeric"
            placeholder="908716325"
            value={form.number}
            onChange={(value) => set('number', value)}
            error={shown('number')}
          />
          <TextField
            label="Dígito"
            required
            hint="O dígito verificador da conta: 1 ou 2 caracteres. Alguns bancos usam X."
            maxLength={2}
            placeholder="2"
            value={form.digit}
            onChange={(value) => set('digit', value)}
            error={shown('digit')}
          />
        </div>

        {banks.isError && (
          <Notice tone="warning">
            Não foi possível carregar a lista de bancos agora. Tente novamente em instantes.
          </Notice>
        )}

        {saveError && (
          <Notice tone="danger">
            {saveError instanceof ApiError ? saveError.detail : 'Não foi possível salvar a conta.'}
          </Notice>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Btn>
        <Btn variant="primary" icon={Icons.check} onClick={() => void submit()} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar conta'}
        </Btn>
      </div>
    </ModalShell>
  );
}
